const STORAGE_PREFIX = 'argus:employees:workforce-layout:v1';
const VALID_LOCATION_KEY = /^(object|object_group):[1-9]\d*$/;

function positiveInteger(value) {
    const number = Number(value);
    return Number.isInteger(number) && number > 0 ? number : null;
}

function normalizeLocationKey(value) {
    const key = String(value || '').trim();
    return VALID_LOCATION_KEY.test(key) ? key : null;
}

function locationKey(item) {
    if (!item) return null;
    const explicit = normalizeLocationKey(item.layout_key || item.location_key);
    if (explicit) return explicit;
    const type = String(item.target_type || item.type || '');
    const id = positiveInteger(
        item.target_id ||
        item.building_id ||
        item.object_group_id ||
        item.id
    );
    return id && (type === 'object' || type === 'object_group')
        ? `${type}:${id}`
        : null;
}

function employeeCount(item) {
    return Array.isArray(item && item.employees) ? item.employees.length : 0;
}

function locationTitle(item) {
    return String(item && (item.title || item.name) || '');
}

function compareLocations(left, right) {
    const countDifference = employeeCount(right) - employeeCount(left);
    if (countDifference) return countDifference;
    const titleDifference = locationTitle(left).localeCompare(
        locationTitle(right),
        'ru',
        {sensitivity: 'base'}
    );
    if (titleDifference) return titleDifference;
    return String(locationKey(left) || '').localeCompare(
        String(locationKey(right) || '')
    );
}

function compareLocationsByName(left, right) {
    const titleDifference = locationTitle(left).localeCompare(
        locationTitle(right),
        'ru',
        {sensitivity: 'base'}
    );
    if (titleDifference) return titleDifference;
    const countDifference = employeeCount(right) - employeeCount(left);
    if (countDifference) return countDifference;
    return String(locationKey(left) || '').localeCompare(
        String(locationKey(right) || '')
    );
}

function sortLocationsByHeadcount(items) {
    return (Array.isArray(items) ? items : []).slice().sort(compareLocations);
}

function sortLocations(items, sortBy, direction) {
    const source = Array.isArray(items) ? items : [];
    const comparator = sortBy === 'name'
        ? compareLocationsByName
        : compareLocations;
    const multiplier = direction === 'asc' ? 1 : -1;
    const naturalDirection = sortBy === 'name' ? 1 : -1;
    return source.slice().sort((left, right) => (
        comparator(left, right) * multiplier * naturalDirection
    ));
}

function normalizeOrder(order) {
    const seen = {};
    return (Array.isArray(order) ? order : []).reduce((result, value) => {
        const key = normalizeLocationKey(value);
        if (!key || seen[key]) return result;
        seen[key] = true;
        result.push(key);
        return result;
    }, []);
}

function arrangeLocations(items, manualOrder, sortBy, direction) {
    const automatic = sortLocations(
        items,
        sortBy === 'name' ? 'name' : 'headcount',
        direction === 'asc' ? 'asc' : 'desc'
    );
    const order = normalizeOrder(manualOrder);
    if (!order.length) return automatic;
    const rank = order.reduce((result, key, index) => {
        result[key] = index;
        return result;
    }, {});
    const known = [];
    const fresh = [];
    automatic.forEach(item => {
        const key = locationKey(item);
        if (key && Object.prototype.hasOwnProperty.call(rank, key)) {
            known.push(item);
        } else {
            fresh.push(item);
        }
    });
    known.sort((left, right) => (
        rank[locationKey(left)] - rank[locationKey(right)]
    ));
    return known.concat(fresh);
}

function reorderLocationKeys(keys, sourceKey, targetKey, position) {
    const order = normalizeOrder(keys);
    const source = normalizeLocationKey(sourceKey);
    const target = normalizeLocationKey(targetKey);
    if (
        !source ||
        !target ||
        source === target ||
        order.indexOf(source) === -1 ||
        order.indexOf(target) === -1
    ) {
        return order;
    }
    const result = order.filter(key => key !== source);
    const targetIndex = result.indexOf(target);
    result.splice(
        targetIndex + (position === 'after' ? 1 : 0),
        0,
        source
    );
    return result;
}

function storageKey(user) {
    if (!user) return null;
    const company = String(user.company || '').trim();
    const userId = positiveInteger(user.userId || user.user_id || user.id);
    return company && userId
        ? `${STORAGE_PREFIX}:${company}:${userId}`
        : null;
}

function readLayout(storage, user) {
    const key = storageKey(user);
    if (!key || !storage || typeof storage.getItem !== 'function') {
        return {mode: 'headcount', order: []};
    }
    try {
        const parsed = JSON.parse(storage.getItem(key) || 'null');
        if (!parsed || parsed.mode !== 'manual') {
            return {mode: 'headcount', order: []};
        }
        return {
            mode: 'manual',
            order: normalizeOrder(parsed.order)
        };
    } catch (error) {
        return {mode: 'headcount', order: []};
    }
}

function saveManualLayout(storage, user, order) {
    const key = storageKey(user);
    if (!key || !storage || typeof storage.setItem !== 'function') return false;
    try {
        storage.setItem(key, JSON.stringify({
            mode: 'manual',
            order: normalizeOrder(order)
        }));
        return true;
    } catch (error) {
        return false;
    }
}

function clearLayout(storage, user) {
    const key = storageKey(user);
    if (!key || !storage || typeof storage.removeItem !== 'function') return false;
    try {
        storage.removeItem(key);
        return true;
    } catch (error) {
        return false;
    }
}

module.exports = {
    STORAGE_PREFIX,
    arrangeLocations,
    clearLayout,
    locationKey,
    normalizeOrder,
    readLayout,
    reorderLocationKeys,
    saveManualLayout,
    sortLocations,
    sortLocationsByHeadcount,
    storageKey
};
